import type { FileCategoryValue, SignUploadResponse } from '@renovation-claim/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from './api';
import { useMe } from './auth';

export type CaseFile = {
  id: string;
  case_id: string;
  storage_key: string;
  filename: string;
  content_type: string;
  size_bytes: string; // bigint encoded as string by Kysely
  category: FileCategoryValue | null;
  ai_category: FileCategoryValue | null;
  ai_confidence: string | null; // numeric encoded as string
  ai_summary: string | null;
  user_confirmed_at: string | null;
  extraction_status: 'pending' | 'extracting' | 'extracted' | 'failed' | 'skipped';
  extraction_error: string | null;
  extracted_event_count: number;
  uploaded_at: string;
};

export const filesQueryKey = (caseId: string) => ['cases', caseId, 'files'] as const;
export const sourcesQueryKey = (caseId: string) => ['cases', caseId, 'sources'] as const;

export type FeedbackSource = {
  message_id: string;
  created_at: string;
  snippet: string;
  shapedEvents: Array<{ id: string; date: string; type: string; summary: string }>;
};

/** A `CaseFile` augmented with the count of timeline events it's
 *  linked to as documentary_proof. Only present on the `/sources`
 *  endpoint response — the regular `/files` endpoint omits it. */
export type CaseFileWithLinks = CaseFile & { linkedEventCount: number };

export type SourcesResponse = {
  files: CaseFileWithLinks[];
  feedback: FeedbackSource[];
};

/**
 * The Sources tab unioned response: files + user-feedback chat messages
 * that have grounded a timeline event. See `GET /cases/:id/sources` on
 * the api side.
 */
export function useSources(caseId: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: sourcesQueryKey(caseId ?? ''),
    queryFn: () => apiFetch<SourcesResponse>(`/cases/${caseId}/sources`),
    enabled: !!caseId && !!me.data?.user,
  });
}

export function useFiles(caseId: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: filesQueryKey(caseId ?? ''),
    queryFn: () => apiFetch<{ files: CaseFile[] }>(`/cases/${caseId}/files`),
    select: (d) => d.files,
    enabled: !!caseId && !!me.data?.user,
    // Poll while categorization is in flight (any file with no ai_category).
    refetchInterval: (q) => {
      const data = q.state.data?.files;
      if (!data) return false;
      // Poll while AI categorization or content extraction is in flight.
      const pending = data.some(
        (f) =>
          f.ai_category === null ||
          f.extraction_status === 'pending' ||
          f.extraction_status === 'extracting',
      );
      return pending ? 2_000 : false;
    },
  });
}

/**
 * Full upload flow: sign URL → PUT to R2/MinIO → register row.
 * The api auto-categorizes on register; the file list refetches via
 * invalidation and polls until AI finishes.
 */
export function useUploadFile(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      // 1. Sign
      const { url, key } = await apiFetch<SignUploadResponse>(
        `/cases/${caseId}/files/sign-upload`,
        {
          method: 'POST',
          body: {
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            contentLength: file.size,
            kind: 'document', // for now everything is "document"; refined later
          },
        },
      );

      // 2. PUT directly to R2/MinIO (NOT through our api).
      // The signed URL has Content-Type and Content-Length baked in — must match.
      const putRes = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putRes.ok) {
        throw new ApiError(`upload failed: ${putRes.status}`, putRes.status);
      }

      // 3. Register the row server-side.
      return await apiFetch<{ file: CaseFile }>(`/cases/${caseId}/files`, {
        method: 'POST',
        body: {
          storageKey: key,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: filesQueryKey(caseId) }),
  });
}

export function useUpdateFileCategory(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, category }: { fileId: string; category: FileCategoryValue }) =>
      apiFetch<{ ok: true }>(`/cases/${caseId}/files/${fileId}`, {
        method: 'PATCH',
        body: { category },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: filesQueryKey(caseId) }),
  });
}

export function useConfirmFile(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) =>
      apiFetch<{ ok: true }>(`/cases/${caseId}/files/${fileId}/confirm`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: filesQueryKey(caseId) }),
  });
}

export function useDeleteFile(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) =>
      apiFetch<{ ok: true }>(`/cases/${caseId}/files/${fileId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: filesQueryKey(caseId) }),
  });
}

/**
 * Mint a short-lived signed GET URL so the timeline footnote modal can
 * preview the source document inline (PDF/image) or stream it for
 * download (text/whatsapp). The bucket is private; the URL is the only
 * way the browser can read the bytes.
 */
export function useFileViewUrl(caseId: string, fileId: string | null | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: ['cases', caseId, 'files', fileId, 'view-url'] as const,
    queryFn: () =>
      apiFetch<{ url: string; expiresIn: number; filename: string; contentType: string }>(
        `/cases/${caseId}/files/${fileId}/view-url`,
      ),
    enabled: !!caseId && !!fileId && !!me.data?.user,
    // The URL has a 5-minute TTL; refetch a bit before that to keep the
    // modal usable across a long read.
    staleTime: 4 * 60 * 1000,
  });
}

/**
 * Reset a file's extraction state and re-trigger the dispatcher. Used when
 * extraction lands in `failed`/`skipped` and the user wants another go
 * (transient AI hiccup, oversized-file fix, etc.).
 */
export function useRetryFileExtraction(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) =>
      apiFetch<{ ok: true; status: string }>(`/cases/${caseId}/files/${fileId}/retry-extraction`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: filesQueryKey(caseId) }),
  });
}
